import { format, isValid, parseISO, startOfToday } from "date-fns";
import { ja } from "date-fns/locale";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SkippedDateAtView } from "../../hooks/api/useReservations";
import { useRepeatSkippedDates } from "../../hooks/api/useReservations";
import { useDateStore } from "../../stores/dateStore";

interface RecurringSkippedDatesSectionProps {
  repeatId: string;
  seatLabel: string;
}

const REASON_DETAILS: Record<SkippedDateAtView["reason"], string> = {
  user_already_booked: "あなたが同日に別座席を予約済み",
  seat_taken: "他の人がこの座席を予約",
  unknown: "作成時に取得できませんでした（現在は空席の可能性）",
};

/** repeatId を DOM の id/aria 属性に安全に埋め込むためのサニタイズ */
function sanitizeForDomId(value: string): string {
  return value.replace(/[^a-zA-Z0-9-]/g, "");
}

export function RecurringSkippedDatesSection({
  repeatId,
  seatLabel,
}: RecurringSkippedDatesSectionProps) {
  const {
    data: skippedDates,
    isLoading,
    isError,
  } = useRepeatSkippedDates(repeatId);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const setSelectedDate = useDateStore((s) => s.setSelectedDate);

  // 防御的二重フィルタ: バックエンドが JST 0:00 基準で過去日を除外済みだが、
  // ユーザーのローカルTZ が JST より西（UTC-N）の場合、JST で「今日」の日付が
  // ローカルでは「明日」に見える可能性がある。ここではローカル TZ の startOfToday
  // で追加フィルタし、ユーザー視点で「過去」のスキップ日を UI に表示しないようにする。
  // parseISO が不正な日付文字列を返す可能性にも備え、isValid でガードする。
  const today = startOfToday();
  const futureSkippedDates = skippedDates?.filter((s) => {
    const parsed = parseISO(s.date);
    return isValid(parsed) && parsed >= today;
  });

  const domId = `skipped-dates-${sanitizeForDomId(repeatId)}`;

  if (isError) {
    return (
      <div className="mt-3 border-t border-gray-100 pt-3">
        <span className="text-sm text-gray-400">
          スキップ情報を取得できませんでした
        </span>
      </div>
    );
  }

  if (isLoading || !futureSkippedDates || futureSkippedDates.length === 0) {
    return null;
  }

  const handleViewDate = (dateStr: string) => {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) {
      return;
    }
    setSelectedDate(parsed);
    navigate("/seats");
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="inline-flex items-center gap-1.5 rounded-full bg-warning-50 px-3 py-1 text-sm font-medium text-warning-700 hover:bg-warning-100 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-warning-500"
        aria-expanded={isExpanded}
        aria-controls={domId}
        aria-label={`${seatLabel} のスキップ ${futureSkippedDates.length}件を${isExpanded ? "閉じる" : "展開"}`}
      >
        <span aria-hidden="true">&#x23F8;</span>
        スキップ {futureSkippedDates.length}件
      </button>

      <ul id={domId} className="mt-2 space-y-1.5" hidden={!isExpanded}>
        {futureSkippedDates.map((skipped) => (
          <li
            key={skipped.date}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-2">
              <span className="text-gray-700">
                {format(parseISO(skipped.date), "M月d日（E）", {
                  locale: ja,
                })}
              </span>
              <span className="text-gray-400" aria-hidden="true">
                —
              </span>
              <span className="text-gray-600">
                {REASON_DETAILS[skipped.reason]}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDate(skipped.date);
              }}
              className="shrink-0 rounded text-primary-600 hover:text-primary-800 font-medium text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
              aria-label={`${format(parseISO(skipped.date), "M月d日", { locale: ja })}の座席マップを表示`}
            >
              &rarr; 表示
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
